import { useEffect, useRef, useState, useMemo } from 'react'
import { Container, Box, Typography, Button, CircularProgress, Alert, Slider } from '@mui/material'
import { useQuizSession } from '../hooks/useQuizSession'
import QuestionCard from '../components/QuestionCard'
import AnswerCard from '../components/AnswerCard'
import { validateAnswer } from '../../../utils/answerValidation'
import { usePostHog } from '@posthog/react'
import QuizResultsCard from '../components/QuizResultsCard';
import { ChevronsUpDown } from 'lucide-react'
import { Menu, MenuItem, ListSubheader, Divider } from '@mui/material'

// TODO: DRY - Consolidate colors with QuestionCard
const DIFFICULTY_LEVELS = [
  { value: 1, label: 'Easy', color: '#388e3c' },
  { value: 2, label: 'Medium', color: '#689f38' },
  { value: 3, label: 'Hard', color: '#f57c00' },
  { value: 4, label: 'Very Hard', color: '#e64a19' },
  { value: 5, label: 'Impossible', color: '#d32f2f' }
]

const MODE_NAMES = {
  '1-1': 'Easy',
  '1-2': 'First Year',
  '1-3': 'Second Year',
  '1-4': 'Fourth Year',
  '1-5': 'Random',
  '2-2': 'Medium',
  '2-3': 'Third Year',
  '2-4': 'Fifth Year',
  '2-5': 'Sixth Year',
  '3-3': 'Hard',
  '3-4': 'O.W.L.',
  '3-5': 'Seventh Year',
  '4-4': 'Very Hard',
  '4-5': 'N.E.W.T.',
  '5-5': 'Impossible',
}

// Ordered mode list for the dropdown, grouped by feel
const MODE_GROUPS = [
  {
    label: 'Single Difficulties',
    modes: [
      { key: '1-1', name: 'Easy',      range: [1, 1] },
      { key: '2-2', name: 'Medium',    range: [2, 2] },
      { key: '3-3', name: 'Hard',      range: [3, 3] },
      { key: '4-4', name: 'Very Hard', range: [4, 4] },
      { key: '5-5', name: 'Impossible',range: [5, 5] },
    ],
  },
  {
    label: 'Ranges',
    modes: [
      { key: '1-2', name: 'First Year',   range: [1, 2] },
      { key: '1-3', name: 'Second Year',  range: [1, 3] },
      { key: '2-3', name: 'Third Year',   range: [2, 3] },
      { key: '1-4', name: 'Fourth Year',  range: [1, 4] },
      { key: '2-4', name: 'Fifth Year',   range: [2, 4] },
      { key: '2-5', name: 'Sixth Year',   range: [2, 5] },
      { key: '3-5', name: 'Seventh Year', range: [3, 5] },
      { key: '3-4', name: 'O.W.L.',       range: [3, 4] },
      { key: '4-5', name: 'N.E.W.T.',     range: [4, 5] },
      { key: '1-5', name: 'Random',       range: [1, 5] },
    ],
  },
]

function DifficultyRangeSelector({ difficultyRange, onChange, modeName }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handlePillClick = (e) => setAnchorEl(e.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const handleModeSelect = (range) => {
    onChange(range)
    handleClose()
  }

  return (
    <Box mt={4} mb={2} px={3}>
      <Typography variant="subtitle1" gutterBottom align="center" sx={{ display: 'block' }}>
        <Box component="span" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
          Difficulty Mode:{' '}
        </Box>
        <Box
          component="span"
          onClick={handlePillClick}
          sx={{
            color: '#ba68c8',
            border: '1px solid #ce93d8',
            px: 1.5,
            py: 0.3,
            borderRadius: '12px',
            fontWeight: 600,
            fontSize: '1.05rem',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            userSelect: 'none',
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(186,104,200,0.08)',
            },
          }}
        >
          {modeName}
          <ChevronsUpDown size={14} style={{ opacity: 0.7 }} />
        </Box>
      </Typography>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 180,
              maxHeight: 450,
              '& .MuiMenuItem-root': {
                fontSize: '0.9rem',
              },
            },
          },
        }}
      >
        {MODE_GROUPS.map((group, gi) => [
          gi > 0 && <Divider key={`divider-${gi}`} />,
          <ListSubheader key={group.label} sx={{ lineHeight: '32px', fontSize: '0.75rem' }}>
            {group.label}
          </ListSubheader>,
          ...group.modes.map((mode) => (
            <MenuItem
              key={mode.key}
              selected={modeName === mode.name}
              onClick={() => handleModeSelect(mode.range)}
              sx={{
                pl: 2,
                '&.Mui-selected': {
                  color: '#7b2f87',
                  fontWeight: 700,
                },
              }}
            >
              {mode.name}
            </MenuItem>
          )),
        ])}
      </Menu>

      <Slider
        value={difficultyRange}
        onChange={(_, newValue) => onChange(newValue)}
        min={1}
        max={5}
        step={1}
        marks={DIFFICULTY_LEVELS.map(l => ({ value: l.value, label: l.label }))}
        valueLabelDisplay="off"
        disableSwap
        sx={{
          '& .MuiSlider-track': {
            background: 'linear-gradient(to right, #388e3c, #d32f2f)',
          },
          '& .MuiSlider-thumb[data-index="0"]': {
            backgroundColor: DIFFICULTY_LEVELS[difficultyRange[0] - 1].color,
          },
          '& .MuiSlider-thumb[data-index="1"]': {
            backgroundColor: DIFFICULTY_LEVELS[difficultyRange[1] - 1].color,
          },
        }}
      />
    </Box>
  )
}

function ReviewCarousel({ answers }) {
  const [reviewIndex, setReviewIndex] = useState(0)

  if (!answers.length) return null

  return (
    <Box mt={2}>
      <Typography align="center" color="text.secondary" mb={2}>
        Review your answers ({reviewIndex + 1} of {answers.length})
      </Typography>
      <AnswerCard
        lastAnswer={answers[reviewIndex]}
        onNext={() => setReviewIndex(i => Math.min(i + 1, answers.length - 1))}
        onPrev={() => setReviewIndex(i => Math.max(i - 1, 0))}
        currentIndex={reviewIndex}
        totalQuestions={answers.length}
        nextLabel="Next"
      />
    </Box>
  )
}

export default function Quiz({ user, onUpdateUser }) {
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log('🎨 Quiz render #', renderCount.current);
  
  const prevUserRef = useRef(user);
  const prevOnUpdateRef = useRef(onUpdateUser);
  
  useEffect(() => {
    if (prevUserRef.current !== user) {
      console.log('🔄 Quiz: user prop changed', { prev: prevUserRef.current, next: user });
      prevUserRef.current = user;
    }
    if (prevOnUpdateRef.current !== onUpdateUser) {
      console.log('🔄 Quiz: onUpdateUser prop changed');
      prevOnUpdateRef.current = onUpdateUser;
    }
  });

  // Also track actual unmounts vs re-renders
  useEffect(() => {
    console.log('✅ Quiz MOUNTED (effect)', renderCount.current);
    return () => console.log('❌ Quiz UNMOUNTED (effect)', renderCount.current);
  }, []);
  
  const [difficultyRange, setDifficultyRange] = useState([1, 3])
  const posthog = usePostHog()

  const selectedDifficulties = useMemo(
    () => Array.from({ length: difficultyRange[1] - difficultyRange[0] + 1 }, (_, i) => difficultyRange[0] + i),
    [difficultyRange]
  )

  const modeName = MODE_NAMES[`${difficultyRange[0]}-${difficultyRange[1]}`] ?? 'Custom'

  const {
    questions,
    currentQuestion,
    currentIndex,
    totalQuestions,
    isQuizComplete,
    remainingQuestionsCount,
    isLoading,
    error,
    initialized,
    answers,
    currentAnswer,
    hasAnswered,
    score,
    submitAnswer,
    goToNextQuestion,
    goToPrevQuestion,
    resetQuiz,
  } = useQuizSession(user, onUpdateUser, 10, selectedDifficulties)

  const scoreSavedRef = useRef(false)

  // Save leaderboard once after finishing
  useEffect(() => {
    if (isQuizComplete && !scoreSavedRef.current) {
      if (user?.username) {
        saveScore(user.username, score)
      }
      posthog?.capture('quiz_completed', {
        score,
        total_questions: totalQuestions,
        mode: modeName,
        difficulty_range: difficultyRange,
      })
      scoreSavedRef.current = true
    }
  }, [isQuizComplete, score, user?.username])

  const handleRestart = () => {
    scoreSavedRef.current = false
    posthog?.capture('quiz_restarted', { mode: modeName })
    resetQuiz()
  }

  const handleSubmitAnswer = (userInput) => {
    const result = submitAnswer(userInput, validateAnswer)

    if (!result.success) return

    // If this was last question → finishing logic lives inside hook
    if (currentIndex === totalQuestions - 1) {
      // goToNextQuestion()
    }
  }

  // Still loading questions
  if (isLoading || !initialized) {
    return (
      <Container maxWidth="sm">
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  // Error loading questions
  if (error) {
    return (
      <Container maxWidth="sm">
        <Box mt={8}>
          <Alert severity="error">Failed to load questions: {error.message}</Alert>
        </Box>
      </Container>
    )
  }

  // No questions available
  if (!currentQuestion && !isQuizComplete) {
    return (
      <Container maxWidth="sm">
        <Box mt={4}>
          <Typography align="center" color="text.secondary" gutterBottom>
            No questions available for selected difficulties
          </Typography>
          <DifficultyRangeSelector
            difficultyRange={difficultyRange}
            onChange={setDifficultyRange}
            modeName={modeName}
          />
        </Box>
      </Container>
    )
  }

  // Quiz finished
  if (isQuizComplete) {
    const reviewAnswers = Object.values(answers);
    return (
      <>
        <QuizResultsCard
          score={score}
          totalQuestions={totalQuestions}
          modeName={modeName}
          answers={reviewAnswers}
          user={user}
          onPlayAgain={handleRestart}
        />
        <Container maxWidth="sm">
          <ReviewCarousel answers={reviewAnswers} />
        </Container>
      </>
    );
  }

  // Normal quiz flow
  return (
    <Container maxWidth="sm">
      {/* Difficulty Selector */}
      <DifficultyRangeSelector
        difficultyRange={difficultyRange}
        onChange={setDifficultyRange}
        modeName={modeName}
      />

      <Box mt={4}>
        {hasAnswered ? (
          <AnswerCard
            lastAnswer={currentAnswer}
            onNext={goToNextQuestion}
            onPrev={goToPrevQuestion}
            currentIndex={currentIndex}
            totalQuestions={totalQuestions}
          />
        ) : (
          <QuestionCard
            question={currentQuestion}
            onSubmit={handleSubmitAnswer}
            onNext={goToNextQuestion}
            onPrev={goToPrevQuestion}
            currentIndex={currentIndex}
            totalQuestions={totalQuestions}
            user={user}
            hasAnswered={hasAnswered}
          />
        )}

        <Typography align="center" color="text.secondary" mt={4}>
          Question {currentIndex + 1} of {totalQuestions}
        </Typography>
      </Box>
    </Container>
  )
}

const saveScore = (username, score) => {
  try {
    const leaderboard = JSON.parse(localStorage.getItem('leaderboard')) || []
    leaderboard.push({ username, score, date: new Date().toISOString() })
    localStorage.setItem('leaderboard', JSON.stringify(leaderboard))
  } catch (e) {
    console.error('Failed to save leaderboard:', e)
  }
}